'use client';

import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import _ from 'lodash';
import { useWillStore } from '@/lib/store';
import { StepWrapper } from '../StepWrapper';
import { User, Users, Heart, Briefcase, Gift, ChevronDown, Check, ShieldCheck, Baby } from 'lucide-react';
import { Button } from '@/app/components/ui/Button';
import { Modal } from '@/app/components/ui/Modal';
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import { toTitleCase } from '@/lib/utils';
import { SaveProgressModal } from '@/app/components/SaveProgressModal';
import { SignInModal } from '@/app/components/auth/SignInModal';
import { createClient } from '@/lib/supabase/client';
import { isEstateSectionComplete } from '@/lib/will-logic';
import { generateWillDiff, WillDiff } from '@/lib/diff-utils';
import { transformToFlat } from '@/lib/transformers';
import { AmendmentSummaryModal } from '@/app/components/AmendmentSummaryModal';

const stripePromise = loadStripe(process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY!);

// Helper component for review sections
const ReviewSection = ({
  title,
  icon,
  children,
  onEdit,
  defaultOpen = true,
  isComplete = true,
  optional = false,
  hideStatus = false,
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  onEdit?: () => void;
  defaultOpen?: boolean;
  isComplete?: boolean;
  optional?: boolean;
  hideStatus?: boolean;
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className={`group bg-white rounded-xl overflow-hidden transition-all duration-300 ${isComplete ? 'shadow-sm border border-gray-100 hover:shadow-md' : optional ? 'border border-amber-100 bg-amber-50/30' : 'border border-gray-200 bg-gray-50/50'}`}>
      <div
        className={`flex items-center justify-between p-5 cursor-pointer transition-colors ${isOpen ? 'border-b border-gray-50' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center gap-4">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${isComplete ? 'bg-primary/5 text-primary' : optional ? 'bg-amber-100/50 text-amber-600' : 'bg-gray-100 text-gray-400'}`}>
            {icon}
          </div>
          <div className="flex flex-col gap-1">
            <h3 className={`font-display font-bold text-lg leading-none ${isComplete ? 'text-primary' : optional ? 'text-primary/80' : 'text-muted-foreground'}`}>{title}</h3>

            {!hideStatus && (
              <div className="flex items-center gap-2">
                {isComplete ? (
                  <span className="inline-flex items-center gap-1 text-[11px] font-bold text-teal uppercase tracking-wider">
                    <Check className="w-3 h-3" strokeWidth={3} />
                    Complete
                  </span>
                ) : optional ? (
                  <span className="inline-flex items-center text-[11px] font-bold text-amber-600 uppercase tracking-wider">
                    Optional
                  </span>
                ) : (
                  <span className="inline-flex items-center text-[11px] font-bold text-muted-foreground uppercase tracking-wider">
                    Incomplete
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {onEdit && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
              className="h-8 px-3 text-primary hover:bg-primary/5 hover:text-primary font-medium"
            >
              Edit
            </Button>
          )}
          <div className="text-muted-foreground/50 transition-transform duration-300" style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>
            <ChevronDown className="h-5 w-5" />
          </div>
        </div>
      </div>
      <div className={`grid transition-all duration-300 ease-in-out ${isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}>
        <div className="overflow-hidden">
          <div className="p-5 pt-2 space-y-4">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

const ReviewItem = ({ label, value }: { label: string; value?: string | null }) => {
  if (!value) return null;
  return (
    <div className="flex flex-col sm:flex-row sm:justify-between py-3 border-b border-gray-50 last:border-0 group/item hover:bg-gray-50/50 -mx-5 px-5 transition-colors">
      <span className="text-muted-foreground text-sm font-medium">{label}</span>
      <span className="font-medium text-foreground text-right">{value}</span>
    </div>
  );
};

const formatAddress = (address?: { name?: string; line1?: string; line2?: string; city?: string; postcode?: string }) => {
  if (!address) return null;
  const parts = [address.name, address.line1, address.line2, address.city, address.postcode].filter(Boolean);
  return parts.join(', ');
};

export const ReviewPayment = () => {
  const {
    data,
    setStep,
    setIsEditingFromReview,
    setData,
    setIsInPaymentMode,
    willId,
    isAuthenticated,
    userEmail,
    userId,
    mirrorEnabled,
    mirrorPartnerEmail,
    mirrorGroupId,
    mirrorPartnerWillId,
  } = useWillStore();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [showSaveModal, setShowSaveModal] = useState(false);

  // Amendment state
  const [showAmendmentModal, setShowAmendmentModal] = useState(false);
  const [amendmentDiffs, setAmendmentDiffs] = useState<WillDiff[]>([]);
  const [isSavingAmendments, setIsSavingAmendments] = useState(false);
  const [originalDataSnapshot, setOriginalDataSnapshot] = useState<typeof data | null>(null);
  const hasAmendmentChanges = useMemo(() => {
    if (!originalDataSnapshot || (data.status !== 'paid' && data.status !== 'submitted')) return false;
    const diffs = generateWillDiff(originalDataSnapshot, data);

    // Explicitly include exclusion changes to be safe against missing defaults
    const exclusionsChanged =
      !_.isEqual(originalDataSnapshot.exclusions ?? [], data.exclusions ?? []) ||
      (originalDataSnapshot.exclusionMessage ?? '') !== (data.exclusionMessage ?? '') ||
      (originalDataSnapshot.excludeChoice ?? null) !== (data.excludeChoice ?? null);

    return ((diffs?.length ?? 0) > 0) || exclusionsChanged;
  }, [originalDataSnapshot, data]);

  // Fetch original data snapshot on mount for paid/submitted wills
  useEffect(() => {
    if ((data.status !== 'paid' && data.status !== 'submitted') || !willId || originalDataSnapshot) return;
    const fetchOriginal = async () => {
      const supabase = createClient();

      // 1. Get the current version ID from the will record
      const { data: willRecord } = await supabase
        .from('wills')
        .select('current_version_id, will_data')
        .eq('id', willId)
        .maybeSingle();

      if (!willRecord) return;

      let originalData = willRecord.will_data;

      // 2. If we have a version ID, fetch that specific version's data
      if (willRecord.current_version_id) {
        const { data: versionRecord } = await supabase
          .from('will_versions')
          .select('will_data')
          .eq('id', willRecord.current_version_id)
          .maybeSingle();

        if (versionRecord?.will_data) {
          originalData = versionRecord.will_data;
        }
      }

      if (originalData) {
        // Normalize to flat shape so diffs compare like-for-like with store data
        setOriginalDataSnapshot(transformToFlat(originalData));
      }
    };
    fetchOriginal();
  }, [data.status, willId, originalDataSnapshot]);

  // Sign In Modal state
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [signInModalTitle, setSignInModalTitle] = useState('Sign In');
  const [signInModalEmail, setSignInModalEmail] = useState('');
  const [signInAutoSendOtp, setSignInAutoSendOtp] = useState(false);
  const [hasVerifiedOtp, setHasVerifiedOtp] = useState(false);
  const [accountExistsHandled, setAccountExistsHandled] = useState(false);

  // Marketing preferences
  const [marketingOptOut, setMarketingOptOut] = useState<boolean | null>(null);
  const [marketingLoading, setMarketingLoading] = useState(false);
  const [marketingError, setMarketingError] = useState<string | null>(null);

  const personalDetails = data.personalDetails;
  const children = useMemo(() => data.children || [], [data.children]);
  const guardians = useMemo(() => data.guardians || [], [data.guardians]);
  const pets = useMemo(() => data.pets || [], [data.pets]);
  const petGuardians = useMemo(() => data.petGuardians || [], [data.petGuardians]);
  const beneficiaries = useMemo(() => data.beneficiaries || [], [data.beneficiaries]);
  const executors = useMemo(() => data.executors || [], [data.executors]);
  const substituteExecutors = useMemo(() => data.substituteExecutors || [], [data.substituteExecutors]);
  const gifts = useMemo(() => data.gifts || [], [data.gifts]);
  const funeralWishes = data.funeralWishes;
  const excludeChoice = data.excludeChoice ?? null;
  const exclusionMessage = data.exclusionMessage || '';

  // Funeral wishes modal state
  const [funeralModalOpen, setFuneralModalOpen] = useState(false);
  const [tempFuneralWishes, setTempFuneralWishes] = useState('');
  const [funeralWishesRemoved, setFuneralWishesRemoved] = useState(false);
  const funeralPrevRef = useRef(funeralWishes || '');

  const [tempExclusionMessage, setTempExclusionMessage] = useState(exclusionMessage);
  const [exclusionModalOpen, setExclusionModalOpen] = useState(false);
  const [exclusionsRemoved, setExclusionsRemoved] = useState(false);
  const exclusionPrevRef = useRef(exclusionMessage || '');

  const [activeTab, setActiveTab] = useState<'summary' | 'people' | 'status' | 'account'>('summary');
  
  // Track if we need to auto-trigger payment after Google login
  const [shouldAutoPayment, setShouldAutoPayment] = useState(false);

  const buildMirrorPayload = useCallback(() => {
    if (mirrorEnabled !== true) return null;
    const partnerEmail = mirrorPartnerEmail || data.personalDetails?.email || userEmail || '';

    // Start partner will from a clean slate so they complete their own answers
    const partnerWill = {
      personalDetails: {
        fullName: '',
        email: partnerEmail,
        dateOfBirth: '',
        address: {
          line1: '',
          line2: '',
          city: '',
          postcode: '',
        },
      },
      hasChildren: undefined,
      children: [],
      hasPets: undefined,
      pets: [],
      guardians: [],
      petGuardians: [],
      guardianConfirmations: {},
      executors: [],
      substituteExecutors: [],
      beneficiaries: [],
      gifts: [],
      disasterFallback: undefined,
      funeralWishes: '',
      exclusions: [],
      exclusionMessage: '',
      excludeChoice: null,
      additionalInstructions: '',
      manuallyAddedPeople: [],
    };

    return {
      enabled: true,
      partnerEmail,
      mirrorGroupId: mirrorGroupId || undefined,
      partnerWillId: mirrorPartnerWillId || undefined,
      partnerWill,
    };
  }, [mirrorEnabled, data, mirrorPartnerEmail, userEmail, mirrorGroupId, mirrorPartnerWillId]);

  // Check for auto_payment query param on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const isAutoPayment = params.get('auto_payment') === 'true';
    
    if (isAutoPayment) {
      // Clear the param from URL to prevent re-triggering on refresh
      const newUrl = window.location.pathname + window.location.search.replace(/[\?&]auto_payment=true/, '').replace(/^&/, '?');
      window.history.replaceState({}, '', newUrl);
      setShouldAutoPayment(true);
    }
  }, []);

  // Allow deep-linking to a specific tab via ?tab=<name>
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tabParam = params.get('tab');
    if (tabParam === 'summary' || tabParam === 'people' || tabParam === 'status' || tabParam === 'account') {
      setActiveTab(tabParam);
    }
  }, []);

  // Persist the current tab in the URL to make sharing/returning easier
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    params.set('tab', activeTab);
    const nextUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState({}, '', nextUrl);
  }, [activeTab]);

  useEffect(() => {
    setTempExclusionMessage(exclusionMessage);
  }, [exclusionMessage]);

  // Track deletions for display cues
  useEffect(() => {
    const current = funeralWishes || '';
    if (funeralPrevRef.current && !current) {
      setFuneralWishesRemoved(true);
    }
    if (current) {
      setFuneralWishesRemoved(false);
    }
    funeralPrevRef.current = current;
  }, [funeralWishes]);

  useEffect(() => {
    const current = exclusionMessage || '';
    if (exclusionPrevRef.current && !current) {
      setExclusionsRemoved(true);
    }
    if (current) {
      setExclusionsRemoved(false);
    }
    exclusionPrevRef.current = current;
  }, [exclusionMessage]);

  // Completion calculation (4 core sections at 25% each)
  const isAboutComplete =
    !!personalDetails?.fullName &&
    personalDetails?.understandsSurvivorship === true;

  const isGuardiansComplete =
    (data.hasChildren === true || data.hasChildren === false) &&
    (data.hasPets === true || data.hasPets === false) &&
    (data.hasChildren === false || children.length > 0) &&
    (data.hasPets === false || pets.length > 0);

  const isEstateComplete = isEstateSectionComplete(data);
  const isExecutorsComplete = (executors.length ?? 0) > 0;

  const completedCoreSteps = [isAboutComplete, isGuardiansComplete, isEstateComplete, isExecutorsComplete].filter(Boolean).length;
  const completionPercent = completedCoreSteps * 25;

  // Fetch marketing preference when viewing Account tab
  useEffect(() => {
    const fetchMarketing = async () => {
      if (!isAuthenticated || !userId) return;
      setMarketingLoading(true);
      setMarketingError(null);
      try {
        const supabase = createClient();
        const { data: customer, error: fetchError } = await supabase
          .from('customers')
          .select('marketing_opt_out')
          .eq('id', userId)
          .maybeSingle();
        if (fetchError) throw fetchError;
        setMarketingOptOut(Boolean(customer?.marketing_opt_out));
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unable to load marketing preference.';
        setMarketingError(errorMessage);
      } finally {
        setMarketingLoading(false);
      }
    };
    if (activeTab === 'account') {
      fetchMarketing();
    }
  }, [activeTab, isAuthenticated, userId]);

  const updateMarketingPreference = async (optOut: boolean) => {
    if (!isAuthenticated || !userId) {
      setMarketingError('Sign in to manage marketing preferences.');
      return;
    }
    setMarketingLoading(true);
    setMarketingError(null);
    try {
      const supabase = createClient();
      const { error: updateError } = await supabase
        .from('customers')
        .upsert({
          id: userId,
          marketing_opt_out: optOut,
        }, {
          onConflict: 'id',
        });
      if (updateError) throw updateError;
      setMarketingOptOut(optOut);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update preference.';
      setMarketingError(errorMessage);
    } finally {
      setMarketingLoading(false);
    }
  };

  // Aggregate all people in the will
  const peopleInWill = useMemo(() => {
    const people = new Map<string, Set<string>>();

    const addPerson = (name: string | undefined | null, role: string) => {
      if (!name || !name.trim()) return;
      const cleanName = name.trim();
      const key = cleanName.toLowerCase();

      if (!people.has(key)) {
        people.set(key, new Set());
      }
      people.get(key)!.add(role);
    };

    // Spouse/Partner
    if (personalDetails?.spouseFullName) {
      addPerson(personalDetails.spouseFullName, 'Spouse/Partner');
    }
    if (personalDetails?.intendedPartnerFullName) {
      addPerson(personalDetails.intendedPartnerFullName, 'Intended Partner');
    }

    children.forEach(c => addPerson(c.fullName, 'Child'));
    guardians.forEach(g => addPerson(g.fullName, 'Guardian'));

    beneficiaries.forEach(b => {
      if (b.relationship === 'Charity') {
        addPerson(b.fullName, 'Charity Beneficiary');
      } else {
        addPerson(b.fullName, 'Beneficiary');
      }
      // Check substitutes
      if (b.substitutes) {
        b.substitutes.forEach((s: { fullName?: string }) => addPerson(s.fullName, 'Substitute Beneficiary'));
      }
    });

    executors.forEach(e => addPerson(e.fullName || e.companyName, 'Executor'));
    substituteExecutors.forEach(e => addPerson(e.fullName || e.companyName, 'Substitute Executor'));
    petGuardians.forEach(p => addPerson(p.fullName, 'Pet Guardian'));

    gifts.forEach(g => {
      const recipient = g.recipient as { fullName?: string } | undefined;
      if (recipient && recipient.fullName) {
        addPerson(recipient.fullName, 'Gift Recipient');
      }
      const substitute = g.substituteRecipient as { fullName?: string } | undefined;
      if (substitute && substitute.fullName) {
        addPerson(substitute.fullName, 'Substitute Gift Recipient');
      }
    });

    return Array.from(people.entries()).map(([key, roles]) => {
      return {
        name: toTitleCase(key),
        roles: Array.from(roles).sort()
      };
    }).sort((a, b) => a.name.localeCompare(b.name));

  }, [personalDetails, children, guardians, beneficiaries, executors, substituteExecutors, petGuardians, gifts]);

  const normalizedStatus = (data.status || 'draft').toLowerCase();
  const isSubmittedOrPaid = normalizedStatus === 'paid' || normalizedStatus === 'submitted';
  const statusLabel = normalizedStatus === 'paid'
    ? 'Completed & Paid'
    : normalizedStatus === 'submitted'
      ? 'Submitted'
      : 'Draft In Progress';
  const statusDescription = isSubmittedOrPaid
    ? 'Your Will is with our specialists. You can still tweak any section before download.'
    : 'Finish the required sections and payment to generate your document.';
  const timelineSteps = useMemo(() => ([
    {
      title: 'Payment & Processing',
      description: 'Secure payment confirmed and queued for specialist review.',
      state: isSubmittedOrPaid ? 'done' : 'current',
    },
    {
      title: 'Download & Print',
      description: isSubmittedOrPaid
        ? 'We’ll email you when your PDF is ready to download.'
        : 'Available after you complete payment.',
      state: isSubmittedOrPaid ? 'next' : 'upcoming',
    },
    {
      title: 'Sign & Witness',
      description: 'Follow our signing checklist to make it legally binding.',
      state: 'upcoming',
    },
  ]), [isSubmittedOrPaid]);

  // Check if will is complete (all required sections have data)
  const isWillComplete =
    // About You
    !!personalDetails?.fullName &&
    personalDetails?.understandsSurvivorship === true &&
    // Guardians
    (data.hasChildren === true || data.hasChildren === false) &&
    (data.hasPets === true || data.hasPets === false) &&
    (data.hasChildren === false || children.length > 0) &&
    (data.hasPets === false || pets.length > 0) &&
    // Your Estate
    isEstateSectionComplete(data) &&
    // Executors
    executors.length > 0;

  const openFuneralModal = () => {
    setTempFuneralWishes(funeralWishes || '');
    setFuneralModalOpen(true);
    setFuneralWishesRemoved(false);
  };

  const openExclusionModal = () => {
    setTempExclusionMessage(exclusionMessage);
    setExclusionChoice('yes');
    setExclusionModalOpen(true);
  };

  const saveFuneralWishes = () => {
    setData({ funeralWishes: tempFuneralWishes });
    setFuneralWishesRemoved(false);
    setFuneralModalOpen(false);
  };

  const setExclusionChoice = (choice: 'yes' | 'no' | null) => {
    if (choice === 'no') {
      setTempExclusionMessage('');
      setExclusionsRemoved(true);
      setData({ excludeChoice: 'no', exclusionMessage: '' });
      setExclusionModalOpen(false);
      return;
    }

    if (choice === 'yes') {
      const trimmed = (tempExclusionMessage || '').trim();
      setData({ excludeChoice: 'yes', exclusionMessage: trimmed });
      setExclusionsRemoved(false);
      return;
    }

    setTempExclusionMessage('');
    setExclusionsRemoved(true);
    setData({ excludeChoice: null, exclusionMessage: '' });
    setExclusionModalOpen(false);
  };

  const saveExclusionMessage = () => {
    const trimmed = (tempExclusionMessage || '').trim();
    if (!trimmed) return;
    setTempExclusionMessage(trimmed);
    setData({ excludeChoice: 'yes', exclusionMessage: trimmed });
    setExclusionsRemoved(false);
    setExclusionModalOpen(false);
  };

  const deleteExclusion = () => {
    setTempExclusionMessage('');
    setExclusionChoice(null);
    setExclusionsRemoved(true);
  };

  const handleSaveAmendmentsClick = async () => {
    console.log('handleSaveAmendmentsClick called. willId:', willId);
    if (!hasAmendmentChanges) {
      return; // Should never be clickable, but guard just in case
    }
    if (!willId) {
      console.error('No willId found in store');
      return;
    }
    setLoading(true);
    try {
      // Fetch current remote data to compare
      const supabase = createClient();
      console.log('Fetching remote will data for id:', willId);

      // First get the current version ID
      const { data: willRecord, error: willError } = await supabase
        .from('wills')
        .select('current_version_id, will_data')
        .eq('id', willId)
        .maybeSingle();

      if (willError) {
        console.error('Supabase error fetching will:', willError);
        throw willError;
      }

      if (!willRecord) {
        console.error('No will found for id:', willId);
        setError('Could not find your will. Please refresh and try again.');
        return;
      }

      let remoteData = willRecord.will_data || {};

      // If we have a version ID, fetch THAT version's data as the baseline
      if (willRecord.current_version_id) {
        const { data: versionRecord, error: versionError } = await supabase
          .from('will_versions')
          .select('will_data')
          .eq('id', willRecord.current_version_id)
          .maybeSingle();

        if (!versionError && versionRecord) {
          remoteData = versionRecord.will_data || {};
          console.log('Using versioned data as baseline');
        }
      }

      console.log('Remote will data fetched successfully');
      const flatRemoteData = transformToFlat(remoteData);
      const diffs = generateWillDiff(flatRemoteData, data);

      // Ensure exclusions changes are captured even if defaults differ
      const exclusionsChanged =
        !_.isEqual(flatRemoteData?.exclusions ?? [], data.exclusions ?? []) ||
        (flatRemoteData?.exclusionMessage ?? '') !== (data.exclusionMessage ?? '') ||
        (flatRemoteData?.excludeChoice ?? null) !== (data.excludeChoice ?? null);

      if (exclusionsChanged && !diffs.some((d) => d.section === 'Exclusions')) {
        diffs.push({ section: 'Exclusions', changes: ['Exclusions updated'] });
      }

      setAmendmentDiffs(diffs);
      setShowAmendmentModal(true);
    } catch (err) {
      console.error('Failed to check for amendments:', err);
      setError('Failed to check for changes. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const confirmSaveAmendments = async () => {
    if (!willId) return;
    setIsSavingAmendments(true);
    setError(null); // Clear previous errors

    try {
      // Use the new amendment API which handles versioning
      const response = await fetch('/api/will/amend', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          willId,
          willData: data
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to save amendments');
      }

      console.log('Amendment saved successfully, version:', result.version);

      // Update local snapshot to current data so we don't prompt again immediately
      setOriginalDataSnapshot(_.cloneDeep(data));

      setShowAmendmentModal(false);



      // Optional: Show success toast or message

    } catch (err) {
      console.error('Failed to save amendments:', err);
      // Show the specific error message from the API (e.g. rate limit warning)
      const errorMessage = err instanceof Error ? err.message : 'Failed to save amendments. Please try again.';
      setError(errorMessage);
    } finally {
      setIsSavingAmendments(false);
    }
  };



  const handlePaymentClick = () => {
    handlePayment();
  };

  const handlePayment = useCallback(async (emailToUse?: string) => {
    const { isAuthenticated: authNow, userEmail: storeEmail, willId: storeWillId } = useWillStore.getState();

    if (!authNow) {
      setShowSaveModal(true);
      return;
    }

    setLoading(true);
    setError(null);
    console.log('Starting payment flow...');
    try {
      console.log('Sending request to /api/checkout...');
      const mirrorPayload = buildMirrorPayload();
      const payload = {
        ...data,
        willId: storeWillId,
        email: emailToUse || data.personalDetails?.email || storeEmail, // Ensure email is passed, fallback to auth email
        ...(mirrorPayload ? { mirror: mirrorPayload } : {}),
      };
      const response = await fetch('/api/checkout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      console.log('Response received status:', response.status);
      const result = await response.json();
      console.log('Response data:', result);

      if (!response.ok) {
        const errorMsg = result.details
          ? `Validation failed: ${result.details.join(', ')}`
          : result.error || 'Payment initialization failed';

        const conflict =
          response.status === 409 ||
          (result.error && result.error.toLowerCase().includes('account already')) ||
          (errorMsg && errorMsg.toLowerCase().includes('account already'));

        // Gracefully handle existing account without throwing an error
        if (conflict) {
          if (hasVerifiedOtp || authNow || showSignInModal || accountExistsHandled) {
            setError('Please retry payment in a moment.');
            return;
          }
          setShowSaveModal(false);
          setSignInModalTitle('Account already created for this email address');
          setSignInModalEmail(emailToUse || data.personalDetails?.email || userEmail || '');
          setSignInAutoSendOtp(true);
          setHasVerifiedOtp(false);
          setAccountExistsHandled(true);
          setShowSignInModal(true);
          return;
        }

        setError(errorMsg);
        return;
      }

      if (result.clientSecret) {
        console.log('Setting client secret...');
        setClientSecret(result.clientSecret);
        setIsInPaymentMode(true);
      } else if (result.successUrl) {
        window.location.href = result.successUrl;
      } else {
        throw new Error('No client secret or redirect URL returned');
      }

    } catch (err) {
      console.error('Payment Error:', err);

      // Handle "Account already exists" (409 Conflict)
      const errorMessage = err instanceof Error ? err.message : '';
      if (errorMessage && errorMessage.includes('Account already exists')) {
        // Avoid re-opening the modal if it's already visible or we've just verified
        if (hasVerifiedOtp || authNow || showSignInModal || accountExistsHandled) {
          setError('Please retry payment in a moment.');
          return;
        }
        setShowSaveModal(false); // Close the save modal if open
        setSignInModalTitle('Account already created for this email address');
        setSignInModalEmail(emailToUse || data.personalDetails?.email || userEmail || '');
        setSignInAutoSendOtp(true);
        setHasVerifiedOtp(false);
        setAccountExistsHandled(true);
        setShowSignInModal(true);
        return;
      }

      setError(errorMessage || 'An unexpected error occurred.');
    } finally {
      setLoading(false);
    }
  }, [data, buildMirrorPayload, hasVerifiedOtp, showSignInModal, accountExistsHandled, userEmail, setIsInPaymentMode]);

  // Auto-trigger payment after Google login redirect
  useEffect(() => {
    if (shouldAutoPayment && isAuthenticated && isWillComplete && data.status !== 'paid' && data.status !== 'submitted' && !loading) {
      console.log('Auto-triggering payment flow from URL param');
      setShouldAutoPayment(false);
      handlePayment();
    }
  }, [shouldAutoPayment, isAuthenticated, isWillComplete, data.status, loading, handlePayment]);

  const options = useMemo(() => {
    return clientSecret ? { clientSecret } : undefined;
  }, [clientSecret]);

  if (clientSecret && options) {
    return (
      <StepWrapper
        title={<span className="flex items-center gap-2"><ShieldCheck className="h-6 w-6 text-primary" />Secure Payment</span>}
      >
        <button
          onClick={() => {
            setClientSecret(null);
            setIsInPaymentMode(false);
          }}
          className="text-sm text-primary hover:underline mb-4 flex items-center gap-1"
        >
          ← Back to Your Will
        </button>
        <div className="w-full max-w-5xl mx-auto">
          <EmbeddedCheckoutProvider stripe={stripePromise} options={options}>
            <EmbeddedCheckout className="w-full" />
          </EmbeddedCheckoutProvider>
        </div>
      </StepWrapper>
    );
  }

  return (
    <StepWrapper
      title={
        <div className="flex flex-col gap-3 w-full md:flex-row md:items-center md:justify-between">
          <span className="flex items-center gap-3">
            My Will
            <span className={`text-sm font-medium px-2.5 py-0.5 rounded-full ${data.status === 'paid' || data.status === 'submitted'
              ? 'bg-teal/10 text-teal'
              : 'bg-amber-100 text-amber-700'
              }`}>
              {data.status || 'Draft'}
            </span>
          </span>
          <span className="text-sm font-normal text-muted-foreground flex items-center gap-2">
            {isAuthenticated ? (
              <>
                <div className="w-2 h-2 rounded-full bg-green-500" />
                Logged in as: <span className="font-medium text-foreground">{toTitleCase(data.personalDetails?.fullName || userEmail || 'User')}</span>
              </>
            ) : (
              <>
                <div className="w-2 h-2 rounded-full bg-gray-300" />
                Not logged in
              </>
            )}
          </span>
        </div>
      }
    >
      <div className="flex flex-col lg:flex-row lg:gap-10">
        <div className="flex-1 space-y-6">
          {error && (
            <div className="p-4 bg-destructive/10 text-destructive border border-destructive/20 rounded-xl text-sm font-medium">
              {error}
            </div>
          )}
        </div>
      </div>
      <div className="space-y-8">
        <div className="w-full">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 p-1 bg-gray-100/80 rounded-xl">
            <button
              onClick={() => setActiveTab('summary')}
              className={`w-full py-2.5 text-sm font-medium rounded-lg transition-all ${activeTab === 'summary'
                ? 'bg-white text-foreground shadow-sm ring-1 ring-black/5'
                : 'text-muted-foreground hover:text-foreground hover:bg-white/50'
                }`}
            >
              Summary
            </button>
            <button
              onClick={() => setActiveTab('people')}
              className={`w-full py-2.5 text-sm font-medium rounded-lg transition-all ${activeTab === 'people'
                ? 'bg-white text-foreground shadow-sm ring-1 ring-black/5'
                : 'text-muted-foreground hover:text-foreground hover:bg-white/50'
                }`}
            >
              People in my Will
            </button>
            <button
              onClick={() => setActiveTab('status')}
              className={`w-full py-2.5 text-sm font-medium rounded-lg transition-all ${activeTab === 'status'
                ? 'bg-white text-foreground shadow-sm ring-1 ring-black/5'
                : 'text-muted-foreground hover:text-foreground hover:bg-white/50'
                }`}
            >
              Will Status
            </button>
            <button
              onClick={() => setActiveTab('account')}
              className={`w-full py-2.5 text-sm font-medium rounded-lg transition-all ${activeTab === 'account'
                ? 'bg-white text-foreground shadow-sm ring-1 ring-black/5'
                : 'text-muted-foreground hover:text-foreground hover:bg-white/50'
                }`}
            >
              Account
            </button>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row lg:gap-10">
          {/* Left Column - Tab Content */}
          <div className={`${activeTab === 'status' ? 'w-full' : 'w-full lg:w-1/2 lg:max-w-3xl'} space-y-6`}>
            {activeTab === 'summary' && (
              <div className="space-y-4">
                {/* Optional: Gifts & Funeral Wishes (Moved to top) */}
                <ReviewSection
                  title="Optional"
                  icon={<Gift className="h-5 w-5 text-amber-500" />}
                  isComplete={true}
                  optional={false}
                  hideStatus
                >
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <p className="text-lg font-semibold text-foreground">Specific Gifts</p>
                        <span className="text-xs px-2 py-1 rounded-full bg-primary/5 text-primary font-semibold">{gifts.length}</span>
                      </div>
                      <Button
                        variant="outline"
                        size="lg"
                        onClick={() => {
                          setIsEditingFromReview(true);
                          setStep(4);
                        }}
                        className="h-12 text-sm px-5 border-gray-200 hover:bg-gray-50 hover:text-current hover:shadow-none transition-colors"
                      >
                        + Add Gift
                      </Button>
                    </div>

                    {gifts.length > 0 ? (
                      <div className="grid gap-3">
                        {gifts.map((gift, i) => (
                          <div key={i} className="rounded-xl border border-gray-200 bg-white px-4 py-3 flex flex-col gap-2 shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex flex-col gap-1">
                                <div className="flex items-center gap-2">
                                  <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground bg-gray-100 px-2 py-1 rounded-full">
                                    {gift.giftType === 'cash' ? 'Cash' : 'Item'}
                                  </span>
                                  {gift.giftType === 'cash' && (
                                    <span className="text-xs font-semibold text-primary bg-primary/5 px-2 py-1 rounded-full">
                                      £{gift.cashAmount?.toLocaleString('en-GB')}
                                    </span>
                                  )}
                                </div>
                                <p className="font-medium text-foreground">
                                  {gift.giftType === 'cash' ? `£${gift.cashAmount?.toLocaleString('en-GB')}` : gift.description}
                                </p>
                                <p className="text-sm text-muted-foreground">To: {toTitleCase((gift.recipient as { fullName?: string })?.fullName)}</p>
                              </div>
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 text-xs text-primary hover:text-primary/80 px-2"
                                  onClick={() => {
                                    setIsEditingFromReview(true);
                                    useWillStore.getState().setPendingGiftEditIndex(i);
                                    setStep(4);
                                  }}
                                >
                                  Edit
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 text-xs text-destructive hover:text-destructive px-2"
                                  onClick={() => {
                                    const updated = gifts.filter((_, idx) => idx !== i);
                                    setData({ gifts: updated });
                                  }}
                                >
                                  Delete
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="p-6 rounded-xl border border-gray-200 bg-white text-muted-foreground text-lg">
                        No specific gifts added
                      </div>
                    )}

                    <div className="space-y-3 pt-4 border-t border-white/50">
                      <div className="flex items-center justify-between">
                        <p className="text-lg font-semibold text-foreground">Funeral Wishes</p>
                        <div className="flex items-center gap-2">
                          {funeralWishes ? (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={openFuneralModal}
                                className="h-8 text-xs text-primary hover:text-primary/80 px-2"
                              >
                                Edit
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setData({ funeralWishes: '' });
                                  setTempFuneralWishes('');
                                }}
                                className="h-8 text-xs text-destructive hover:text-destructive px-2"
                              >
                                Delete
                              </Button>
                            </>
                          ) : (
                            <Button
                              variant="outline"
                              size="lg"
                              onClick={openFuneralModal}
                              className="h-12 text-sm px-5 border-gray-200 hover:bg-gray-50 hover:text-current hover:shadow-none transition-colors"
                            >
                              + Funeral Wishes
                            </Button>
                          )}
                        </div>
                      </div>

                      {funeralWishes ? (
                        <div className="text-muted-foreground text-lg bg-gray-50/80 p-5 rounded-xl border border-gray-100 leading-relaxed italic">
                          &ldquo;{funeralWishes}&rdquo;
                        </div>
                      ) : funeralWishesRemoved ? (
                        <div className="p-5 rounded-xl border border-amber-200 bg-amber-50 text-amber-900 text-lg font-medium">
                          Funeral Wishes removed
                        </div>
                      ) : (
                        <div className="flex items-center justify-between p-6 rounded-xl border border-gray-200 bg-white">
                          <p className="text-muted-foreground text-lg">Record your preferences for your funeral arrangements here.</p>
                        </div>
                      )}
                    </div>

                    <div className="space-y-3 pt-4 border-t border-white/50">
                      <div className="flex items-center justify-between">
                        <p className="text-lg font-semibold text-foreground">Exclusions</p>
                        <div className="flex items-center gap-2">
                          {exclusionMessage ? (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={openExclusionModal}
                                className="h-8 text-xs text-primary hover:text-primary/80 px-2"
                              >
                                Edit
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={deleteExclusion}
                                className="h-8 text-xs text-destructive hover:text-destructive px-2"
                              >
                                Delete
                              </Button>
                            </>
                          ) : (
                            <Button
                              variant="outline"
                              size="lg"
                              onClick={openExclusionModal}
                              className="h-12 text-sm px-5 border-gray-200 hover:bg-gray-50 hover:text-current hover:shadow-none transition-colors"
                            >
                              + Add exclusions
                            </Button>
                          )}
                        </div>
                      </div>

                      <div className="p-6 rounded-xl border border-gray-200 bg-white text-muted-foreground text-lg">
                        {exclusionMessage
                          ? `"${exclusionMessage}"`
                          : exclusionsRemoved
                            ? 'Exclusions removed'
                            : excludeChoice === 'no'
                              ? 'You have chosen not to include any exclusions.'
                              : 'Add a short note if you want to deliberately exclude someone from your estate.'}
                      </div>
                    </div>
                  </div>
                </ReviewSection>

                {/* Personal Details */}
                <ReviewSection
                  title="About You"
                  icon={<User className="h-5 w-5" />}
                  onEdit={() => {
                    setIsEditingFromReview(true);
                    setStep(0);
                  }}
                  isComplete={!!personalDetails?.fullName}
                >
                  <ReviewItem label="Full Name" value={toTitleCase(personalDetails?.fullName)} />
                  <ReviewItem label="Date of Birth" value={personalDetails?.dateOfBirth} />
                  <ReviewItem label="Address" value={formatAddress(personalDetails?.address)} />
                  <ReviewItem label="Marital Status" value={toTitleCase(personalDetails?.maritalStatus?.replace(/_/g, ' '))} />
                  {personalDetails?.spouseFullName && (
                    <>
                      <ReviewItem label="Spouse/Partner" value={toTitleCase(personalDetails.spouseFullName)} />
                      <ReviewItem label="Lives at same address" value={personalDetails.spouseLivesWithYou === true ? 'Yes' : personalDetails.spouseLivesWithYou === false ? 'No' : null} />
                    </>
                  )}
                  {mirrorEnabled && (
                    <>
                      <ReviewItem label="Mirror Will Included" value="Yes" />
                      {mirrorPartnerEmail && <ReviewItem label="Partner's Email" value={mirrorPartnerEmail} />}
                    </>
                  )}
                </ReviewSection>

                {/* Children & Guardians */}
                <ReviewSection
                  title="Guardians"
                  icon={<Baby className="h-5 w-5" />}
                  onEdit={() => {
                    setIsEditingFromReview(true);
                    setStep(1);
                  }}
                  isComplete={
                    (data.hasChildren === false || children.length > 0) &&
                    (data.hasPets === false || pets.length > 0)
                  }
                >
                  {children.length > 0 ? (
                    <>
                      <p className="text-sm font-medium text-foreground mb-3">Children ({children.length})</p>
                      {children.map((child, i) => (
                        <div key={i} className="pl-4 border-l-2 border-primary/20 mb-3">
                          <p className="font-medium text-foreground">{toTitleCase(child.fullName)}</p>
                          <p className="text-sm text-muted-foreground">Born: {child.dateOfBirth}</p>
                        </div>
                      ))}
                    </>
                  ) : (
                    <p className="text-muted-foreground text-sm italic">No children added</p>
                  )}

                  {guardians.length > 0 && (
                    <>
                      <p className="text-sm font-medium text-foreground mb-3 mt-6">Guardians</p>
                      {/* Group guardians by child */}
                      {(() => {
                        const guardiansByChild = new Map<string, typeof guardians>();
                        const unassignedGuardians: typeof guardians = [];

                        guardians.forEach(g => {
                          const forChild = (g as { forChild?: string }).forChild;
                          if (forChild) {
                            if (!guardiansByChild.has(forChild)) {
                              guardiansByChild.set(forChild, []);
                            }
                            guardiansByChild.get(forChild)!.push(g);
                          } else {
                            unassignedGuardians.push(g);
                          }
                        });

                        if (guardiansByChild.size > 0) {
                          return Array.from(guardiansByChild.entries()).map(([childName, childGuardians]) => (
                            <div key={childName} className="mb-4">
                              <p className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wide">For {childName}:</p>
                              {childGuardians.map((guardian, i) => (
                                <div key={i} className="pl-4 border-l-2 border-primary/20 mb-3 ml-1">
                                  <p className="font-medium text-foreground">{toTitleCase(guardian.fullName)}</p>
                                  <p className="text-sm text-muted-foreground">{formatAddress(guardian.address)}</p>
                                </div>
                              ))}
                            </div>
                          ));
                        } else {
                          return guardians.map((guardian, i) => (
                            <div key={i} className="pl-4 border-l-2 border-primary/20 mb-3">
                              <p className="font-medium text-foreground">{toTitleCase(guardian.fullName)}</p>
                              <p className="text-sm text-muted-foreground">{formatAddress(guardian.address)}</p>
                            </div>
                          ));
                        }
                      })()}
                    </>
                  )}

                  {pets.length > 0 && (
                    <>
                      <p className="text-sm font-medium text-foreground mb-3 mt-6">Pets ({pets.length})</p>
                      {pets.map((pet, i) => (
                        <div key={i} className="pl-4 border-l-2 border-primary/20 mb-3">
                          <p className="font-medium text-foreground">{toTitleCase(pet.name)}</p>
                        </div>
                      ))}
                    </>
                  )}

                  {petGuardians.length > 0 && (
                    <>
                      <p className="text-sm font-medium text-foreground mb-3 mt-6">Pet Guardians ({petGuardians.length})</p>
                      {petGuardians.map((guardian, i) => (
                        <div key={i} className="pl-4 border-l-2 border-primary/20 mb-3">
                          <p className="font-medium text-foreground">{toTitleCase(guardian.fullName)}</p>
                        </div>
                      ))}
                    </>
                  )}
                </ReviewSection>

                {/* Beneficiaries */}
                <ReviewSection
                  title="My Estate"
                  icon={<Heart className="h-5 w-5" />}
                  onEdit={() => {
                    setIsEditingFromReview(true);
                    setStep(2);
                  }}
                  isComplete={beneficiaries.length > 0 && isEstateSectionComplete(data)}
                >
                  {beneficiaries.length > 0 ? (
                    <>
                      <p className="text-sm font-medium text-foreground mb-3">Primary Beneficiaries</p>
                      {beneficiaries.map((ben, i) => (
                        <div key={i} className="pl-4 border-l-2 border-primary/20 mb-4">
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="font-medium text-foreground">{toTitleCase(ben.fullName)}</p>
                              {ben.relationship && ben.relationship !== 'Beneficiary' && (
                                <p className="text-sm text-muted-foreground">{ben.relationship}</p>
                              )}
                              <div className="text-xs text-muted-foreground mt-1.5 bg-gray-50 inline-block px-2 py-1 rounded border border-gray-100">
                                <span className="font-semibold text-foreground/80">Fallback: </span>
                                <span className="italic">
                                  {ben.predeceasePolicy === 'children' && "Pass to their children"}
                                  {ben.predeceasePolicy === 'similar_goals' && "Pass to similar charity"}
                                  {ben.predeceasePolicy === 'substitute' && (
                                    <>
                                      Pass to specific people
                                      {ben.substitutes && ben.substitutes.length > 0 && (
                                        <span className="block not-italic text-muted-foreground pl-0 mt-0.5">
                                          {ben.substitutes.map((s: { fullName: string; percentage: number }) => `${s.fullName} (${s.percentage}%)`).join(', ')}
                                        </span>
                                      )}
                                    </>
                                  )}
                                  {ben.predeceasePolicy === 'redistribute' && "Split between remaining beneficiaries"}
                                  {!ben.predeceasePolicy && <span className="text-amber-600 font-medium not-italic">Not set</span>}
                                </span>
                              </div>
                            </div>
                            <span className="text-lg font-bold text-primary">{ben.percentage}%</span>
                          </div>
                        </div>
                      ))}
                    </>
                  ) : (
                    <p className="text-muted-foreground text-sm italic">No beneficiaries added</p>
                  )}
                </ReviewSection>

                {/* Executors */}
                <ReviewSection
                  title="Executors"
                  icon={<Briefcase className="h-5 w-5" />}
                  onEdit={() => {
                    setIsEditingFromReview(true);
                    setStep(3);
                  }}
                  isComplete={executors.length > 0}
                >
                  {executors.length > 0 ? (
                    <>
                      <p className="text-sm font-medium text-foreground mb-3">Main Executors ({executors.length})</p>
                      {executors.map((exec, i) => (
                        <div key={i} className="pl-4 border-l-2 border-primary/20 mb-3">
                          <p className="font-medium text-foreground">{toTitleCase(exec.fullName) || exec.companyName}</p>
                          <p className="text-sm text-muted-foreground">
                            {exec.relationship === 'Professional' ? 'Professional Executor' : exec.relationship || 'Individual'}
                          </p>
                        </div>
                      ))}
                    </>
                  ) : (
                    <p className="text-muted-foreground text-sm italic">No executors added</p>
                  )}

                  <div className="mt-6 pt-4 border-t border-gray-100">
                    <p className="text-sm font-medium text-foreground mb-3">
                      Substitute Executors: <span className={substituteExecutors.length > 0 ? 'text-teal font-bold' : 'text-muted-foreground'}>{substituteExecutors.length > 0 ? 'Yes' : 'No'}</span>
                    </p>
                    {substituteExecutors.length > 0 && (
                      <>
                        {substituteExecutors.map((exec, i) => (
                          <div key={i} className="pl-4 border-l-2 border-gray-200 mb-3">
                            <p className="font-medium text-foreground">{toTitleCase(exec.fullName) || exec.companyName}</p>
                            <p className="text-sm text-muted-foreground">
                              {exec.relationship === 'Professional' ? 'Professional Executor' : exec.relationship || 'Individual'}
                            </p>
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                </ReviewSection>
              </div>
            )}

            {activeTab === 'people' && (
              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden p-6 md:p-8">
                <h3 className="font-display font-bold text-xl mb-6 text-primary">People in my Will</h3>

                {peopleInWill.length > 0 ? (
                  <div className="space-y-6">
                    <p className="text-muted-foreground leading-relaxed">
                      Below is a list of everyone mentioned in your Will and their assigned roles.
                      To edit any details, please return to the relevant section in the &apos;Review Sections&apos; tab.
                    </p>

                    <div className="divide-y divide-gray-100 border rounded-xl border-gray-100 bg-gray-50/30 overflow-hidden">
                      {peopleInWill.map((person, i) => (
                        <div key={i} className="flex flex-col sm:flex-row sm:justify-between p-5 hover:bg-white transition-colors group">
                          <div className="flex items-center">
                            <span className="font-medium text-foreground text-lg">{person.name}</span>
                          </div>
                          <div className="flex flex-wrap gap-2 mt-3 sm:mt-0 sm:justify-end">
                            {person.roles.map((role, r) => (
                              <span key={r} className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-white border border-gray-200 text-muted-foreground shadow-sm">
                                {role}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Users className="h-8 w-8 text-gray-300" />
                    </div>
                    <p className="text-foreground font-medium text-lg">No people added yet</p>
                    <p className="text-muted-foreground mt-2">People you add to your Will will appear here.</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'status' && (
              <div className="space-y-6">
                <div className="bg-white border border-gray-200 rounded-xl overflow-hidden p-6 md:p-8 space-y-6">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                    <div className="space-y-2">
                      <h3 className="font-display font-bold text-2xl text-primary leading-tight">Will Status</h3>
                      <p className="text-muted-foreground text-sm leading-relaxed">{statusDescription}</p>
                    </div>
                    <span className={`self-start text-sm font-semibold px-3 py-1.5 rounded-full ${isSubmittedOrPaid ? 'bg-teal/10 text-teal' : 'bg-amber-100 text-amber-800'}`}>
                      {statusLabel}
                    </span>
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  </div>

                  {!willId && normalizedStatus === 'draft' && (
                    <div className="p-4 rounded-lg border border-amber-200 bg-amber-50 text-amber-800 text-sm font-medium">
                      Save and complete your details to generate a Will ID we can track.
                    </div>
                  )}

                </div>

                <div className="bg-white border border-gray-200 rounded-xl overflow-hidden p-6 md:p-8 space-y-6">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Next steps</p>
                      <p className="text-foreground font-semibold text-lg">Where you are in the process</p>
                    </div>
                    <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{completionPercent}% core info complete</span>
                  </div>

                  <div className="relative space-y-6 pl-8 before:content-[''] before:absolute before:left-3 before:top-2 before:bottom-2 before:w-0.5 before:bg-gray-100">
                    {timelineSteps.map((step, idx) => {
                      const isDone = step.state === 'done';
                      const isActive = step.state === 'current' || step.state === 'next';
                      const dotClasses = isDone ? 'bg-teal' : isActive ? 'bg-primary' : 'bg-gray-200';
                      const textClasses = isDone || isActive ? 'text-foreground' : 'text-muted-foreground';

                      return (
                        <div key={idx} className="relative">
                          <div className={`absolute -left-[34px] w-7 h-7 rounded-full border-4 border-white shadow-sm ${dotClasses}`} />
                          <h4 className={`text-base font-semibold ${textClasses}`}>{step.title}</h4>
                          <p className="text-sm text-muted-foreground mt-1">{step.description}</p>
                        </div>
                      );
                    })}
                  </div>

                </div>
              </div>
            )}

            {activeTab === 'account' && (
              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden p-6 md:p-8 space-y-5">
                <div className="flex items-center justify-between">
                  <h3 className="font-display font-bold text-xl text-primary">Account</h3>
                  <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{isAuthenticated ? 'Signed in' : 'Guest'}</span>
                </div>
                <div className="space-y-3 text-sm text-foreground">
                  <div className="p-4 rounded-xl border border-gray-100 bg-gray-50/60">
                    <p className="text-muted-foreground font-medium">Full name</p>
                    <p className="font-semibold">{toTitleCase(data.personalDetails?.fullName || '') || 'Not set'}</p>
                  </div>
                  <div className="p-4 rounded-xl border border-gray-100 bg-gray-50/60">
                    <p className="text-muted-foreground font-medium">Email</p>
                    <p className="font-semibold">{userEmail || data.personalDetails?.email || 'Not set'}</p>
                  </div>
                  <div className="p-4 rounded-xl border border-gray-100 bg-gray-50/60">
                    <p className="text-muted-foreground font-medium">Will ID</p>
                    <p className="font-semibold">{willId || 'Not yet saved'}</p>
                  </div>
                  <div className="p-4 rounded-xl border border-gray-100 bg-gray-50/60 space-y-3">
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <p className="text-muted-foreground font-medium">Email marketing</p>
                        <p className="text-xs text-muted-foreground">Product updates, tips, and reminders. You can opt out anytime.</p>
                      </div>
                      <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        {marketingLoading ? 'Updating...' : marketingOptOut === false ? 'Subscribed' : 'Unsubscribed'}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-3">
                      <Button
                        size="sm"
                        variant="primary"
                        disabled={!isAuthenticated || marketingLoading || marketingOptOut === false}
                        onClick={() => updateMarketingPreference(false)}
                        className="h-9"
                      >
                        Subscribe
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={!isAuthenticated || marketingLoading || marketingOptOut === true}
                        onClick={() => updateMarketingPreference(true)}
                        className="h-9"
                      >
                        Unsubscribe
                      </Button>
                    </div>
                    {marketingError && <p className="text-xs text-destructive">{marketingError}</p>}
                    {!isAuthenticated && (
                      <p className="text-xs text-muted-foreground">Sign in to manage marketing preferences.</p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Payment Section (Sticky on desktop) - only show on Summary tab */}
          {activeTab === 'summary' && (
            <div className="w-full lg:flex-1 mt-8 lg:mt-0">
              <div className="lg:sticky lg:top-24">
                <div className="glass-card w-full p-8 md:p-10 rounded-2xl border border-white/20 shadow-xl space-y-6 relative overflow-hidden">
                  <div className="space-y-3 text-center">
                    <h3 className="font-display font-medium text-3xl md:text-4xl text-primary">
                      {(data.status === 'paid' || data.status === 'submitted')
                        ? 'Your Will is being Checked'
                        : (isWillComplete ? 'Your Will is Ready' : 'Your Will is not ready')}
                    </h3>
                    <p className="text-muted-foreground leading-relaxed">
                      {(data.status === 'paid' || data.status === 'submitted')
                        ? 'If you would like to make amendments, feel free to do so in the sections and then press the button below.'
                        : (isWillComplete
                          ? 'Review your details, pay, and our specialists will check your Will.'
                          : 'Please complete all required sections above before you can proceed to payment.')}
                    </p>
                  </div>

                  <Button
                    onClick={(data.status === 'paid' || data.status === 'submitted') ? handleSaveAmendmentsClick : handlePaymentClick}
                    disabled={loading || ((data.status === 'paid' || data.status === 'submitted') ? !hasAmendmentChanges : !isWillComplete)}
                    variant="primary"
                    className={`w-full text-lg h-16 transition-all font-bold shadow-lg ${((data.status === 'paid' || data.status === 'submitted') ? hasAmendmentChanges : isWillComplete)
                      ? 'shadow-cta/25 hover:scale-[1.02] active:scale-[0.98]'
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed shadow-none border-gray-200'
                      }`}
                  >
                    {loading
                      ? 'Processing...'
                      : ((data.status === 'paid' || data.status === 'submitted')
                        ? 'Save Amendments'
                        : 'Complete your Will')}
                  </Button>

                  <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground/70 pt-2 border-t border-gray-100 mt-4">
                    <ShieldCheck className="w-4 h-4" />
                    <span>Secure SSL Encrypted Payment</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Amendment Summary Modal */}
      <AmendmentSummaryModal
        isOpen={showAmendmentModal}
        onClose={() => setShowAmendmentModal(false)}
        onSave={confirmSaveAmendments}
        diffs={amendmentDiffs}
        isSaving={isSavingAmendments}
        error={error}
      />

      {/* Funeral Wishes Modal */}
      <Modal isOpen={funeralModalOpen} onClose={() => setFuneralModalOpen(false)} title={funeralWishes ? 'Edit Funeral Wishes' : 'Add Funeral Wishes'}>
        <div className="space-y-4">
          <div className="text-base text-muted-foreground space-y-3 mb-4">
            <p className="leading-relaxed">
              While these wishes are not legally binding, they provide helpful guidance for your loved ones during a difficult time.
            </p>
            <p className="font-semibold text-foreground">You might include:</p>
            <ul className="list-disc list-inside space-y-1 ml-1">
              <li>Whether you prefer burial or cremation</li>
              <li>Specific songs, readings, or hymns for the service</li>
              <li>Where you would like your ashes scattered or interred</li>
              <li>Any religious or cultural customs you&apos;d like observed</li>
            </ul>
          </div>

          <textarea
            value={tempFuneralWishes}
            onChange={(e) => setTempFuneralWishes(e.target.value)}
            className="flex min-h-[180px] w-full rounded-xl border border-input bg-white px-4 py-3 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none shadow-sm"
            placeholder="e.g. I wish to be cremated and have my ashes scattered at my favourite beach. I would like 'What a Wonderful World' played at my service..."
          />

          <div className="pt-2">
            <Button
              onClick={saveFuneralWishes}
              disabled={!tempFuneralWishes.trim()}
              variant="primary"
              className="w-full h-12 text-lg"
            >
              Save Wishes
            </Button>
          </div>
        </div>
      </Modal>
      {/* Exclusions Modal */}
      <Modal isOpen={exclusionModalOpen} onClose={() => setExclusionModalOpen(false)} title="Add Exclusions">
        <div className="space-y-4">
          <div className="text-base text-muted-foreground space-y-3 mb-2">
            <p className="leading-relaxed">
              Use this note to record anyone you are deliberately excluding from your will and the reasons why. Clear reasoning can help your executors defend your wishes if the will is challenged.
            </p>
            <p className="font-semibold text-foreground">You might include:</p>
            <ul className="list-disc list-inside space-y-1 ml-1">
              <li>Who is being excluded (name and relationship)</li>
              <li>Why you have chosen to exclude them</li>
              <li>Any relevant context (e.g. gifts already made, estrangement)</li>
            </ul>
          </div>

          <textarea
            value={tempExclusionMessage}
            onChange={(e) => setTempExclusionMessage(e.target.value)}
            className="flex min-h-[180px] w-full rounded-xl border border-input bg-white px-4 py-3 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none shadow-sm"
            placeholder="I have excluded [Name] because..."
          />

          <div className="flex flex-wrap gap-2 pt-1">
            <Button
              onClick={saveExclusionMessage}
              disabled={!tempExclusionMessage.trim()}
              variant="primary"
              className="h-12 text-base px-5 w-full"
            >
              Save exclusion
            </Button>
          </div>
        </div>
      </Modal>
      {/* Save/Account Creation Modal for Unauthenticated Users */}
      <SaveProgressModal
        isOpen={showSaveModal}
        onClose={() => setShowSaveModal(false)}
        title="Finalise your Account"
        mode="payment"
        onSuccess={(email) => {
          setShowSaveModal(false);
          setHasVerifiedOtp(true);
          // Proceed to payment immediately after "saving" (collecting email)
          handlePayment(email);
        }}
        onSignIn={() => {
          setShowSaveModal(false);
          setSignInModalTitle('Sign In');
          setSignInModalEmail(''); // Let them type it or use default
          setShowSignInModal(true);
        }}
      />

      {/* Sign In Modal for Existing Users */}
      <SignInModal
        isOpen={showSignInModal}
        onClose={() => {
          setShowSignInModal(false);
          setSignInAutoSendOtp(false);
          setAccountExistsHandled(false);
        }}
        title={signInModalTitle}
        initialEmail={signInModalEmail}
        autoSendOtp={signInAutoSendOtp}
        onSuccess={() => {
          setShowSignInModal(false);
          setSignInAutoSendOtp(false);
          setHasVerifiedOtp(true);
          setAccountExistsHandled(true);
          // User is now authenticated, retry payment
          // We pass undefined to use the email from the store/auth context now
          handlePayment();
        }}
      />
    </StepWrapper>
  );
};
